import type { WorkPackagePagesParams } from "@common/api/work-packages"
import type { WorkPackageView } from "@common/client"
import { z } from "zod"
import type { Display } from "@/components/layout/display-options"
import {
  type Column,
  columnSchema,
  DEFAULT_COLUMNS,
  withNumber,
} from "./columns"
import { GROUPINGS, type Grouping, groupingSchema } from "./grouping"
import { KINDS, PRIORITIES, STATUSES } from "./status"

export const TABS = [
  "all",
  "active",
  "aog",
  "mine",
  "archived",
] as const satisfies readonly WorkPackageView[]

const tabSchema = z.enum(TABS)

export type Tab = z.infer<typeof tabSchema>

export const SORTS = [
  "number",
  "description",
  "updated",
] as const satisfies readonly Column[]

const sortSchema = z.enum(SORTS)

export type Sort = z.infer<typeof sortSchema>

const directionSchema = z.enum(["asc", "desc"])

export type Direction = z.infer<typeof directionSchema>

export const workOrdersSearchSchema = z.object({
  tab: tabSchema.optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  status: z.array(z.enum(STATUSES)).optional().catch(undefined),
  priority: z.array(z.enum(PRIORITIES)).optional().catch(undefined),
  kind: z.array(z.enum(KINDS)).optional().catch(undefined),
  subscribed: z.boolean().optional().catch(undefined),
  customer: z.array(z.string()).optional().catch(undefined),
  group: z
    .enum([...GROUPINGS, "none"])
    .optional()
    .catch(undefined),
  subgroup: groupingSchema.optional().catch(undefined),
  columns: z.array(columnSchema).optional().catch(undefined),
  sort: sortSchema.optional().catch(undefined),
  direction: directionSchema.optional().catch(undefined),
})

export type WorkOrdersSearch = z.infer<typeof workOrdersSearchSchema>

export type WorkOrdersDisplay = {
  group: Grouping | undefined
  subgroup: Grouping | undefined
  columns: Column[]
  sort: Sort
  direction: Direction
}

const ORDER_BY: Record<Sort, string> = {
  number: "number",
  description: "description",
  updated: "updated_at",
}

export function tabLabel(tab: Tab): string {
  switch (tab) {
    case "all":
      return "All"
    case "active":
      return "Active"
    case "aog":
      return "AOG"
    case "mine":
      return "Mine"
    case "archived":
      return "Archived"
  }
}

export function groupingSearch(
  next: Display<Grouping>,
): Pick<WorkOrdersSearch, "group" | "subgroup"> {
  const group = next.group ?? "none"
  return {
    group: group === "status" ? undefined : group,
    subgroup: next.subgroup,
  }
}

export function displayOf(search: WorkOrdersSearch): WorkOrdersDisplay {
  const group = search.group === "none" ? undefined : (search.group ?? "status")
  return {
    group,
    subgroup: group && search.subgroup !== group ? search.subgroup : undefined,
    columns: search.columns ? withNumber(search.columns) : DEFAULT_COLUMNS,
    sort: search.sort ?? "updated",
    direction: search.direction ?? (search.sort ? "asc" : "desc"),
  }
}

export function workOrderPagesParams(
  search: WorkOrdersSearch,
  aircraftId?: string,
): WorkPackagePagesParams {
  const display = displayOf(search)
  return {
    view: search.tab ?? "all",
    search: search.search || null,
    status: search.status ?? null,
    priority: search.priority ?? null,
    kind: search.kind ?? null,
    subscribed: search.subscribed ?? null,
    customer_id: search.customer ?? null,
    aircraft_id: aircraftId ?? null,
    order_by: ORDER_BY[display.sort],
    order_direction: display.direction,
    group: display.group ?? null,
    subgroup: display.subgroup ?? null,
  }
}

export function sortSearch(
  display: WorkOrdersDisplay,
  column: Sort,
): Partial<WorkOrdersSearch> {
  const direction =
    column === display.sort && display.direction === "asc" ? "desc" : "asc"
  const byDefault = column === "updated" && direction === "desc"
  return byDefault
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

export function columnsSearch(
  columns: Column[],
  defaults: Column[],
): Partial<WorkOrdersSearch> {
  const unchanged =
    columns.length === defaults.length &&
    columns.every((column) => defaults.includes(column))
  return { columns: unchanged ? undefined : columns }
}
